"use client";

import { Heart, Loader2, MessageCircle } from "lucide-react";
import { ToteIcon } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import DepositNotice from "@/components/product-detail/DepositNotice";
import type { DepositMethod } from "@/types";
import type { SupportedCurrency } from "@/lib/utils/currency";

interface DesktopBookingActionsProps {
  cartItemCount: number;
  isAddingToCart: boolean;
  hasMissingRequiredOptions?: boolean;
  isChatLoading: boolean;
  isWishlisted: boolean;
  isWishlistLoading: boolean;
  onCheckout: () => void;
  onAddToCart: () => void;
  onChatClick?: () => void;
  onWishlistClick: () => void;
  /** 보증금 안내 (설정된 방식 없으면 미표시) */
  depositMethods?: DepositMethod[];
  depositAmount?: number | null;
  sourceCurrency: SupportedCurrency;
}

export default function DesktopBookingActions({
  cartItemCount,
  isAddingToCart,
  hasMissingRequiredOptions = false,
  isChatLoading,
  isWishlisted,
  isWishlistLoading,
  onCheckout,
  onAddToCart,
  onChatClick,
  onWishlistClick,
  depositMethods,
  depositAmount,
  sourceCurrency,
}: DesktopBookingActionsProps) {
  const t = useTranslations("product.booking");

  return (
    <div className="space-y-3">
      {/* 총액은 선택 내역 박스(CartItemsList)에 표시 — 여기선 CTA만 */}
      {hasMissingRequiredOptions && (
        <p className="text-xs text-red-600 text-center">
          {t("missingRequiredOptions") || "필수 옵션을 선택해주세요"}
        </p>
      )}

      <Button
        className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white text-base font-bold rounded-xl"
        disabled={cartItemCount === 0}
        onClick={onCheckout}
      >
        {t("reserve")}
      </Button>

      <div className="grid grid-cols-3 gap-2">
        <Button
          variant="outline"
          className="w-full min-w-0 h-10 gap-1 rounded-xl border-gray-300 px-1 has-[>svg]:px-1 text-xs"
          onClick={onChatClick}
          disabled={isChatLoading}
        >
          <MessageCircle className="h-4 w-4" />
          {t("chatInquiry")}
        </Button>

        <Button
          variant="outline"
          className="w-full min-w-0 h-10 gap-1 rounded-xl border-gray-300 px-1 has-[>svg]:px-1 text-xs"
          onClick={onWishlistClick}
          disabled={isWishlistLoading}
        >
          <Heart
            className={cn(
              "h-4 w-4",
              isWishlisted && "fill-red-500 text-red-500",
            )}
          />
          {t("wishlist")}
        </Button>

        <Button
          variant="outline"
          className="w-full min-w-0 h-10 gap-1 rounded-xl border-gray-300 px-1 has-[>svg]:px-1 text-xs"
          disabled={cartItemCount === 0 || isAddingToCart}
          onClick={onAddToCart}
        >
          {isAddingToCart ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <ToteIcon className="h-4 w-4" />
              {t("addToCart")}
            </>
          )}
        </Button>
      </div>

      {/* 보증금 안내 — PC는 버튼들 하단에 표시 (확정 목업) */}
      <DepositNotice
        methods={depositMethods}
        depositAmount={depositAmount}
        sourceCurrency={sourceCurrency}
        className="mt-3.5"
      />
    </div>
  );
}
